import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Briefcase, FolderKanban, FileSpreadsheet, ChevronDown, Check } from 'lucide-react';
import { usePersona, PERSONAS, PERSONA_CONFIG } from '../context/PersonaContext';

const PERSONA_ICONS = {
  [PERSONAS.EXECUTIVE]: Building2,
  [PERSONAS.PORTFOLIO_MANAGER]: Briefcase,
  [PERSONAS.PROGRAM_MANAGER]: FolderKanban,
  [PERSONAS.PROJECT_MANAGER]: FileSpreadsheet
};

const PersonaSwitcher = () => {
  const navigate = useNavigate();
  const { persona, setPersona, getCurrentConfig } = usePersona();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const currentConfig = getCurrentConfig();
  const CurrentIcon = PERSONA_ICONS[persona] || Building2;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePersonaChange = (newPersona) => {
    setPersona(newPersona);
    setIsOpen(false);
    const config = PERSONA_CONFIG[newPersona];
    if (config?.defaultRoute) {
      navigate(config.defaultRoute);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors"
        data-testid="persona-switcher"
      >
        <CurrentIcon className="w-4 h-4 text-[var(--clr-text-secondary)]" />
        <span className="text-sm font-medium text-gray-700">{currentConfig.label}</span>
        <ChevronDown className={`w-4 h-4 text-[var(--clr-text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-[var(--clr-surface-card)] rounded-lg shadow-lg border z-50" data-testid="persona-dropdown">
          <div className="p-2">
            <p className="text-xs font-semibold text-[var(--clr-text-muted)] uppercase px-2 py-1">Switch Role</p>
            {Object.entries(PERSONA_CONFIG).map(([key, config]) => {
              const Icon = PERSONA_ICONS[key] || Building2;
              const isSelected = persona === key;
              
              return (
                <button
                  key={key}
                  onClick={() => handlePersonaChange(key)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                    isSelected 
                      ? 'bg-[var(--clr-brand-primary)] text-[var(--clr-text-on-brand)]' 
                      : 'hover:bg-[var(--clr-surface-hover)] text-[var(--clr-text-primary)]'
                  }`}
                  data-testid={`persona-option-${key}`}
                >
                  <Icon className={`w-4 h-4 ${isSelected ? 'text-[var(--clr-text-on-brand)]' : 'text-[var(--clr-text-muted)]'}`} />
                  <span className="flex-1 text-sm font-medium">{config.label}</span>
                  {isSelected && <Check className="w-4 h-4 text-[var(--clr-text-on-brand)]" />}
                </button>
              );
            })}
          </div>
          
          <div className="border-t px-3 py-2">
            <p className="text-xs text-[var(--clr-text-muted)]">
              Default zoom: <span className="font-medium capitalize">{currentConfig.defaultZoom}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonaSwitcher;
